import UnfoldLessIcon from "@mui/icons-material/UnfoldLess";
import UnfoldMoreIcon from "@mui/icons-material/UnfoldMore";
import { Box, IconButton } from "@mui/material";
import type { DateTime } from "luxon";
import { type FC, useCallback, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../../../contexts/AuthProvider";
import { FEATURE_FLAGS } from "../../../contexts/FeatureFlagsProvider";
import { useHospitalData } from "../../../contexts/HospitalDataProvider";
import { useWeekViewContext } from "../../../contexts/WeekView";
import { useFeatureFlag } from "../../../hooks/useOpenFeatureFlag";
import { localStorageService } from "../../../services/LocalStorageService";
import { NEUTRAL } from "../../../theme/tokens";
import type { StaffingIssue } from "../../../utils/autoStaffing";
import { isShiftDayInPast } from "../../../utils/dateUtils";
import type { DayItem, NurseInShift, PunchSummaryCategory } from "../../../utils/models";
import { DayViewItem } from "../DayViewItem/DayViewItem";
import { WeeklyGapView } from "../WeeklyGapView/WeeklyGapView";

type WeeklyScheduleProps = {
	weekIndex: number;
	recommendations: StaffingIssue[];
	searchText: string | null;
	onSetSearch: (name: string) => void;
	selectedContractSections: ReadonlySet<string>;
};

export const WeeklySchedule: FC<WeeklyScheduleProps> = ({ weekIndex, searchText, recommendations, onSetSearch, selectedContractSections }) => {
	const { weekViewData, visibleWeekViewData, costlyShiftsByDay, shiftHoursById, actualShiftIds } = useWeekViewContext();
	const { getWeekIndexByDate, chosenDepartment, timezone } = useHospitalData();
	const { userInfo } = useAuth();
	const userId = userInfo?.id;
	const navigate = useNavigate();
	const futureLookingWeeklyOvertimeEnabled = useFeatureFlag(FEATURE_FLAGS.FutureLookingWeeklyOvertime);
	const isDynamicWeekStartEnabled = useFeatureFlag(FEATURE_FLAGS.StaffingDynamicWeekStart);
	const renderedWeekViewData = isDynamicWeekStartEnabled ? visibleWeekViewData : weekViewData;
	// DEFAULT: userId should always be present here — this component only renders on already-authenticated,
	// department-scoped routes. The `userId ? ... : false` fallback is defensive-only, not a real code path.
	const [isDayDetailsCollapsed, setIsDayDetailsCollapsed] = useState(() => (userId ? localStorageService.getWeeklyGapsDaySummaryCollapsed(userId) : false));

	const [activeDay, setActiveDay] = useState<DateTime | null>(null);
	const [activeCategory, setActiveCategory] = useState<PunchSummaryCategory | null>(null);

	const onDayClicked = useCallback(
		(date: DateTime) => navigate(`/${chosenDepartment.department_id}/day-schedule/${date.toFormat("yyyy-MM-dd")}`),
		[navigate, chosenDepartment],
	);

	const onCostlyShiftDoubleClick = useCallback((shift: NurseInShift) => onSetSearch(shift.name), [onSetSearch]);

	const handleActiveCategoryChange = useCallback(
		(date: DateTime, category: PunchSummaryCategory) => {
			const isSameSelection = !!activeDay && activeDay.hasSame(date, "day") && activeCategory === category;
			if (isSameSelection) {
				setActiveDay(null);
				setActiveCategory(null);
				return;
			}
			setActiveDay(date);
			setActiveCategory(category);
		},
		[activeDay, activeCategory],
	);

	// The day the Annie "post an open shift" walkthrough spotlights: the first the Post Shift tab will
	// accept, asked of the same predicate the tab itself uses.
	//
	// No fallback when nothing matches. A week with no postable day must yield NO anchor, or the launch
	// scan grabs a stale one mid-navigation and runs the tour on the outgoing week.
	const postShiftAnchorDate = useMemo<DateTime | undefined>(
		() => renderedWeekViewData.find((dayItem: DayItem) => !isShiftDayInPast(dayItem.date, timezone))?.date,
		[renderedWeekViewData, timezone],
	);

	const handleActionCenterClose = useCallback((): void => {
		setActiveDay(null);
		setActiveCategory(null);
	}, []);

	if (!renderedWeekViewData.length) return null;

	return (
		<Box data-testid="weekly-view-root" sx={{ display: "flex", flexDirection: "column", height: "100%", minHeight: 0, overflow: "hidden" }}>
			<Box sx={{ display: "flex", flexShrink: 0 }}>
				{renderedWeekViewData.map((dayItem: DayItem) => {
					const iso = dayItem.date.toISODate();
					const isActive = !!activeDay && dayItem.date.hasSame(activeDay, "day");
					return (
						<DayViewItem
							key={dayItem.date.toSeconds()}
							dayItem={dayItem}
							recommendations={recommendations}
							weekIndex={getWeekIndexByDate(dayItem.date) ?? weekIndex}
							searchText={searchText}
							onDayClicked={onDayClicked}
							isCollapsed={isDayDetailsCollapsed}
							costlyStaffShifts={futureLookingWeeklyOvertimeEnabled ? (costlyShiftsByDay.get(iso ?? "") ?? []) : undefined}
							shiftHoursById={futureLookingWeeklyOvertimeEnabled ? shiftHoursById : undefined}
							actualShiftIds={futureLookingWeeklyOvertimeEnabled ? actualShiftIds : undefined}
							onCostlyShiftDoubleClick={onCostlyShiftDoubleClick}
							onActiveCategoryChange={handleActiveCategoryChange}
							activeCategory={isActive ? activeCategory : null}
							onActionCenterClose={isActive ? handleActionCenterClose : undefined}
							onSetSearch={onSetSearch}
							isPostShiftAnchor={!!postShiftAnchorDate && dayItem.date.hasSame(postShiftAnchorDate, "day")}
						/>
					);
				})}
			</Box>

			<Box
				sx={{
					display: "flex",
					alignItems: "center",
					justifyContent: "center",
					py: 0.25,
					borderTop: `1px solid ${NEUTRAL[300]}`,
					borderBottom: `1px solid ${NEUTRAL[300]}`,
					bgcolor: NEUTRAL[100],
					cursor: "pointer",
					"&:hover": { bgcolor: NEUTRAL[150] },
					transition: "background-color 0.15s ease",
					flexShrink: 0,
				}}
				onClick={() =>
					setIsDayDetailsCollapsed((prev) => {
						const next = !prev;
						// DEFAULT: userId should always be present here — this component only renders on
						// already-authenticated, department-scoped routes. This guard is defensive-only.
						if (userId) {
							localStorageService.setWeeklyGapsDaySummaryCollapsed(next, userId);
						}
						return next;
					})
				}
			>
				<IconButton size="small" disableRipple sx={{ p: 0.25, color: NEUTRAL[500] }} data-testid="weekly-view-toggle-day-details">
					{isDayDetailsCollapsed ? <UnfoldMoreIcon sx={{ fontSize: 13 }} /> : <UnfoldLessIcon sx={{ fontSize: 13 }} />}
				</IconButton>
			</Box>

			<Box data-testid="weekly-view-gap-container" sx={{ background: "white", flex: 1, pt: 3, pb: 0.5, px: 2, minHeight: 0, overflow: "hidden" }}>
				<WeeklyGapView searchText={searchText} onSetSearch={onSetSearch} selectedContractSections={selectedContractSections} />
			</Box>
		</Box>
	);
};
